import {
  SPEC_MICROS_PER_CENT,
  SPEC_MICROS_PER_CREDIT,
  type SpecBootstrap,
  type SpecMemberSeatState,
  type SpecUsageSummary,
} from '@parcel/types';
import { getWorkspaceScopedPath } from '../components/app-sidebar-nav.js';
import {
  formatSpecBillingDate,
  formatSpecCreditAmount,
  specCreditsFromMicros,
} from './spec-billing.js';

/**
 * What chat is allowed to say and do about this workspace's Spec balance.
 *
 * One derivation, two readers: `computeCanSend` in the runtime provider (which
 * decides whether Send is offered at all) and `SpecUsageBanner` (which says
 * why). A second, independently written notion of "out of credits" is exactly
 * what shipped before this: `composer.tsx` compared `includedRemaining === 0`
 * and knew nothing about the purchased balance or the member's own seat.
 *
 * Everything here is derived from what the SERVER reported. There is no
 * subscription status, no Stripe reconciliation state and no run state in the
 * inputs, because:
 *
 *  - past-due / unpaid / canceled are already encoded. `activeIncludedGrants`
 *    filters through `paidSeatCreditsUsable`, so a suspended workspace arrives
 *    with no remaining seat allowance. A purchased balance is reported
 *    separately, while the paid-seat entitlement gate still decides whether a
 *    run may reach the ledger.
 *  - Stripe outbox state is admin-only by a binding constraint and must not
 *    change what a member is told about their entitlement.
 *  - "a run is already going" is runtime state, not a usage fact. The runtime
 *    computes it, and this must not contradict it.
 *
 * Copy is a fixed sentence per state, NEVER derived from a response body: a
 * Spec error body can quote the member's own prompt, and this string is
 * rendered and could be logged. Same rule as `specFailureMessage`.
 */

/** Chat only speaks when Send is refused; a workspace that can pay hears nothing. */
export type SpecUsageGuardState = 'ok' | 'blocked';

/** What the banner's link offers. Every one lands on Workspace > Billing. */
export type SpecUsageGuardCta = 'manage_ai' | 'upgrade_plan' | 'add_seats' | 'buy_credits';

export interface SpecUsageGuard {
  state: SpecUsageGuardState;
  /** Null exactly when `state` is `ok`: a healthy workspace draws no banner. */
  message: string | null;
  cta: SpecUsageGuardCta | null;
}

export interface SpecUsageGuardContext {
  /** Wall clock in epoch milliseconds, for trial expiry and the date copy. */
  now: number;
  /**
   * Whether this member may spend the pooled balance at all. `required` means
   * the workspace bills Spec by AI seat and this member holds none, which the
   * server refuses with `SPEC_SEAT_REQUIRED` before it looks at any balance.
   */
  memberSeat: SpecMemberSeatState;
}

const OK: SpecUsageGuard = { state: 'ok', message: null, cta: null };

const SEAT_REQUIRED: SpecUsageGuard = {
  state: 'blocked',
  message: 'Ask an admin for an AI seat to use Spec.',
  // Only an admin can act on this, so there is nothing to link a member to.
  cta: null,
};

/** Stands in for a credit amount this build could not compute. */
export const SPEC_CREDITS_UNKNOWN = '--';

/**
 * Decimal places a credit amount is displayed to, matching
 * `CREDIT_DISPLAY_MAX_FRACTION_DIGITS` in spec-billing.ts and
 * `CREDIT_DISPLAY_DIGITS` on the billing card.
 */
const SPEC_CREDIT_DISPLAY_DIGITS = 2;

/**
 * A CHARGE for a human, floored at the smallest amount worth printing.
 *
 * Usage display may round; it may not round a real charge down to nothing.
 * `formatSpecCreditAmount` renders 0.004 credits as "0", which reads as "this
 * cost nothing" on a money surface, so anything under the display resolution
 * says so in words instead.
 *
 * Rounds to NEAREST above that floor, which for an amount already spent is the
 * conservative direction: a charge shown as slightly larger than it is never
 * understates what the workspace owes. Use `formatSpecCreditsRemaining` for
 * anything that is a balance or a limit, where the safe direction is the
 * opposite one.
 *
 * An unconvertible amount gets a dash for the same reason as the floor, not
 * "0". Every caller today passes a validated count, so this is unreachable --
 * but the whole point of the rule above is that a zero which is not a zero is
 * the one wrong answer here, and it would have been wrong to make an exception
 * for the case where the number is missing entirely.
 */
export function formatSpecCreditsFromMicros(micros: number): string {
  const credits = specCreditsFromMicros(micros, SPEC_MICROS_PER_CREDIT);
  if (credits == null || !Number.isFinite(credits)) return SPEC_CREDITS_UNKNOWN;
  if (credits > 0 && credits < 0.01) return 'less than 0.01';
  return formatSpecCreditAmount(credits);
}

/**
 * A BALANCE or a LIMIT for a human, FLOORED to the displayed precision.
 *
 * The opposite rounding from the charge above, and deliberately so. 2,765,440
 * microcredits left in a balance rounds to nearest as "2.77", which OVERSTATES
 * the headroom a member has before their next run is refused; flooring says
 * "2.76". Every balance and limit surface must use the same safe direction.
 *
 * Floored in microcredits rather than on the credit float:
 * `Math.floor(2.7654 * 100)` is a question about a binary fraction
 * rather than about credits.
 */
export function formatSpecCreditsRemaining(micros: number): string {
  const credits = specCreditsFromMicros(micros, SPEC_MICROS_PER_CREDIT);
  if (credits == null || !Number.isFinite(credits)) return SPEC_CREDITS_UNKNOWN;
  // Still a real amount, and still not zero: a member with a sliver of a credit
  // left is not out, and must not be told a rounded "0".
  if (credits > 0 && credits < 0.01) return 'less than 0.01';
  const factor = 10 ** SPEC_CREDIT_DISPLAY_DIGITS;
  return formatSpecCreditAmount(Math.floor((micros * factor) / SPEC_MICROS_PER_CREDIT) / factor);
}

/** Dollars, with the cents dropped only when there are none. */
function usdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** A credit balance or limit as dollars, floored to the cent like `formatSpecCreditsRemaining`. */
export function formatSpecBalanceUsd(micros: number): string {
  const credits = specCreditsFromMicros(micros, SPEC_MICROS_PER_CREDIT);
  if (credits == null || !Number.isFinite(credits)) return SPEC_CREDITS_UNKNOWN;
  if (micros > 0 && micros < SPEC_MICROS_PER_CENT) return 'less than $0.01';
  return usdFromCents(Math.floor(micros / SPEC_MICROS_PER_CENT));
}

/** A credit charge as dollars, to the nearest cent and never rounded down to nothing. */
export function formatSpecChargeUsd(micros: number): string {
  const credits = specCreditsFromMicros(micros, SPEC_MICROS_PER_CREDIT);
  if (credits == null || !Number.isFinite(credits)) return SPEC_CREDITS_UNKNOWN;
  if (micros > 0 && micros < SPEC_MICROS_PER_CENT) return 'less than $0.01';
  return usdFromCents(Math.round(micros / SPEC_MICROS_PER_CENT));
}

/** A date sentence, or an empty string when there is no date to promise. */
function onDate(prefix: string, iso: string | null): string {
  const formatted = formatSpecBillingDate(iso);
  return formatted ? ` ${prefix} ${formatted}.` : '';
}

function blocked(message: string, cta: SpecUsageGuardCta): SpecUsageGuard {
  return { state: 'blocked', message, cta };
}

export function deriveSpecUsageGuard(
  summary: SpecUsageSummary,
  context: SpecUsageGuardContext,
): SpecUsageGuard {
  // First, and above every balance. The pool can be full and the workspace
  // healthy while the server still refuses THIS member, so reading the balance
  // first would hand a refused member an enabled composer and a 403.
  if (context.memberSeat === 'required') return SEAT_REQUIRED;

  const kind = summary.entitlementKind;

  /**
   * A purchased balance the server will ACTUALLY draw on, which is narrower
   * than a positive number.
   *
   * Read through `isCount`, not `> 0` alone: `purchasedRemaining` is newer than
   * some deployed API responses, and `undefined > 0` is false -- which would
   * have this guard block a workspace whose balance can fund the run, silently,
   * on exactly the version skew it cannot see. `readableSpecUsage` rejects a
   * response missing the field outright; this is the second line for the
   * callers that hold a typed summary already.
   *
   * The `ai_seats` condition is the one that matters most. `beginRun` calls
   * `ensureSpecEntitlement` BEFORE it reserves anything, and for a seat-billed
   * workspace that routes to `ensurePaidSeats`, which throws
   * `SPEC_PAYMENT_REQUIRED` when the Stripe seat item or period is missing --
   * before the ledger is consulted at all, balance or no balance. That state is
   * visible here as a zero GRANT on an `ai_seats` workspace (a suspended or
   * unsettled subscription makes `activeIncludedGrants` return nothing). So a
   * balance may rescue a workspace whose allowance is SPENT, and never one
   * whose entitlement is suspended: enabling Send there would promise a run
   * that fails on every attempt.
   */
  const hasBalance = isCount(summary.purchasedRemaining) && summary.purchasedRemaining > 0;
  // See `specSpendLimitBlocked` below for why this reads through a shared
  // predicate rather than an inline `<= 0`.
  const spendLimitBlocked = specSpendLimitBlocked(summary);
  const balanceCanFund =
    hasBalance && !(kind === 'ai_seats' && summary.includedGranted === 0) && !spendLimitBlocked;

  /**
   * A trial that ran out of time while the tab sat open.
   *
   * The snapshot still reports credits, because it was taken while the grant
   * was live; the ledger drops that grant the instant `expiresAt` passes. This
   * is the one boundary that is an ENDING. Free and seat periods RENEW at
   * `periodEnd`, so applying the same rule there would tell a workspace it is
   * out of credits at the exact moment it was granted more.
   */
  if (specTrialEnded(summary, context.now)) {
    // The trial grant is gone, but a balance is not tied to it. `ensureTrial`
    // does not throw on an expired trial -- it returns the stale grant, and
    // `availableFunding` then skips it on `expiresAt > now` and draws the
    // balance instead. So the run works, and blocking it here would refuse a
    // send the server would have completed.
    if (balanceCanFund) return OK;
    return blocked('Your free AI trial has ended.', 'add_seats');
  }

  /**
   * A paid workspace whose trial has not been created yet.
   *
   * The entitlement service issues the trial grant on the FIRST MESSAGE and
   * never from a read, so a Starter or Pro workspace that has never sent one
   * reports an `ai_seats`-less plan with nothing granted. Those zeroes are not
   * an exhausted entitlement: blocking them would make the first message
   * unsendable, and no workspace could ever start its trial. An exhausted trial
   * is distinguishable because it has a grant to have exhausted.
   */
  if (kind === 'trial' && summary.includedGranted === 0) return OK;

  const hasIncluded = summary.includedRemaining > 0;

  /**
   * The purchased balance funds a run exactly as the allowance does, subject to
   * the entitlement check above (see `balanceCanFund`).
   *
   * `availableFunding` draws from allowance and balance grants alike, in one
   * pass ordered by expiry (`ledger.ts` `fundableSource`), and only
   * `ai_seat_monthly` is gated on a healthy subscription. So a workspace with
   * an exhausted allowance and a live balance is one the SERVER will happily
   * fund -- and this guard was refusing the send anyway, client-side, with a
   * sentence about credits being used up while the workspace was holding some.
   *
   * The split that made this easy to miss is the one that exists on purpose:
   * `includedRemaining` counts allowance grants and `purchasedRemaining`
   * counts balance grants, because the two must never be summed in the UI. The
   * LEDGER has no such split -- to it they are one pool -- so a guard that
   * predicts what the ledger will do has to read both.
   */

  if (!hasIncluded && !balanceCanFund) {
    /**
     * Never GRANTED, rather than spent, so the sentence must not claim spend.
     *
     * Two states reach here having spent nothing. A first seat purchase whose
     * invoice webhook has not landed: the seat-quantity route clears
     * `payment_healthy` and `seat_grant_paid` on an add, and
     * `paidSeatCreditsUsable` reads that as SUSPENDED, so `activeIncludedGrants`
     * returns nothing and the summary reports zero granted. And a suspended
     * subscription, which arrives the same way. Billing tells the admin the
     * purchase is still syncing with Stripe; telling every seat holder in the
     * same minute that the workspace "used its Parcel credits" asserts a spend
     * that never happened. Free reaches it too, when the `freeGrants` kill
     * switch is off and no monthly grant was ever issued.
     *
     * MUST stay below the unstarted-trial escape above, never before it: the
     * trial grant is created on the FIRST MESSAGE, so an unstarted trial
     * legitimately reports zero granted, and blocking there would make every
     * Starter and Pro first message unsendable.
     */
    if (summary.includedGranted === 0) {
      return blocked(
        'AI usage is not available right now.',
        // The same call to action each kind already had: Free cannot buy AI
        // seats, so its only move stays the plan.
        kind === 'free_monthly' ? 'upgrade_plan' : 'manage_ai',
      );
    }
    /**
     * The balance is not gone, the SPEND LIMIT stopped this run.
     *
     * `reserveSpecCredits` refuses at the limit with `SPEC_SPEND_LIMIT_EXCEEDED`
     * whatever the balance says, so a member here is holding credits that a
     * "used up" sentence would misdescribe -- and would send them to buy more
     * of a thing they already have. Only an owner or admin can raise the
     * limit, so the message says so, unlike every other sentence in this
     * block, which names no one because any member can fix those themselves
     * by waiting for the period or adding seats.
     *
     * Guarded on `hasBalance`: a workspace with no balance at all is out of
     * credits in the ordinary way, and the sentences below are already right
     * for it. `balanceCanFund` being false here cannot be for the
     * `ai_seats && includedGranted === 0` reason -- that already returned
     * above -- so `spendLimitBlocked` is the only remaining way `hasBalance`
     * and `!balanceCanFund` hold together.
     */
    if (spendLimitBlocked && hasBalance) {
      return blocked(
        `You've hit your monthly credit limit. An admin can raise it.${onDate('Resets', summary.spendLimitResetsAt ?? null)}`,
        'manage_ai',
      );
    }
    if (kind === 'free_monthly') {
      // Free receives only the configurable monthly grant: it cannot buy AI
      // seats, so the only move is the plan.
      return blocked(
        `You've used this month's free AI usage.${onDate('Resets', summary.periodEnd)}`,
        'upgrade_plan',
      );
    }
    if (kind === 'trial') {
      return blocked("You've reached your free trial limit.", 'add_seats');
    }
    return blocked(
      `You've reached this month's AI usage.${onDate('Resets', summary.periodEnd)}`,
      'buy_credits',
    );
  }

  // Allowance left, or a balance paying for the overflow: Send works, so chat stays quiet.
  return OK;
}

const ENTITLEMENT_KINDS = new Set<string>(['free_monthly', 'trial', 'ai_seats']);

/** A trial whose end date has passed; the ledger stops drawing its grant at that instant. Shared with the allowance view. */
export function specTrialEnded(summary: SpecUsageSummary, now: number): boolean {
  const endAt = summary.periodEnd ? Date.parse(summary.periodEnd) : Number.NaN;
  return summary.entitlementKind === 'trial' && Number.isFinite(endAt) && endAt <= now;
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Whether the spend limit, not the allowance, has nothing left for this
 * workspace's purchased balance to draw on. `reserveSpecCredits` refuses
 * every draw once this is true, whatever the balance says.
 *
 * Exported so `SpecUsagePopover` classifies the same summary exactly the same
 * way, rather than a second `isCount(...) && ... <= 0` written out beside it:
 * two open-coded copies of the same split is how a guard and a display start
 * disagreeing about whether a workspace has credits, which is the reason this
 * module keeps `balanceSource`-style splits behind one name.
 *
 * `isCount` first, not `<= 0` alone: `spendLimitRemainingMicros` is newer
 * than some deployed API responses, and `undefined <= 0` is false anyway --
 * but stating it through `isCount` is what keeps that true on the next edit.
 * A response missing the field reads as "no limit", which is the correct
 * fail-open direction: see `SpecUsageSummarySchema`.
 */
export function specSpendLimitBlocked(summary: SpecUsageSummary): boolean {
  return isCount(summary.spendLimitRemainingMicros) && summary.spendLimitRemainingMicros <= 0;
}

/**
 * The summary, or null when this build cannot read it.
 *
 * `readSpecBootstrap` passes the usage numbers through UNVALIDATED on purpose,
 * so that a display-only shape change cannot make chat look unavailable. That
 * only holds if the reader checks: every comparison in the guard is false
 * against a string or a NaN, and "no capacity" is precisely what false reads
 * as -- so an unreadable summary would otherwise disable Send for everyone.
 */
/**
 * Exported so a SURFACE can ask the same question the gate does.
 *
 * The popover renders figures off this summary and used to trust it blindly,
 * which meant a response the gate had already judged unreadable still produced
 * a "-- credits" chip over a 0% meter -- a confident "you have nothing" drawn
 * from numbers nobody could read. One check, two callers, no way to disagree.
 */
export function readableSpecUsage(value: unknown): SpecUsageSummary | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.entitlementKind !== 'string' ||
    !ENTITLEMENT_KINDS.has(record.entitlementKind)
  ) {
    return null;
  }
  if (
    !isCount(record.includedGranted) ||
    !isCount(record.includedUsed) ||
    !isCount(record.includedRemaining) ||
    // Newer than some deployed responses, and now load-bearing: it decides
    // whether an exhausted allowance still blocks. A response without it is
    // unreadable rather than a workspace with no balance, so the caller falls
    // open instead of blocking on a number it never received.
    !isCount(record.purchasedRemaining)
  ) {
    return null;
  }
  return value as SpecUsageSummary;
}

/**
 * The guard for a loaded bootstrap, failing OPEN on an unreadable balance and
 * still closed on the member's seat.
 *
 * The two facts have opposite failure costs. Being wrong about the balance
 * costs one clear server refusal; being wrong about the seat in the permissive
 * direction costs the same, but reading an ABSENT balance as empty would lock
 * every member out of every workspace, which is the bug this task exists to
 * delete. The seat state is separately narrowed by `readSpecBootstrap` and so
 * is still trustworthy when the numbers beside it are not.
 */
export function specUsageGuardFor(bootstrap: SpecBootstrap | null, now: number): SpecUsageGuard {
  if (!bootstrap) return OK;
  const summary = readableSpecUsage(bootstrap.usage);
  if (!summary) return bootstrap.memberSeat === 'required' ? SEAT_REQUIRED : OK;
  return deriveSpecUsageGuard(summary, { now, memberSeat: bootstrap.memberSeat });
}

/**
 * Where a usage call to action goes: the workspace's own Billing page, which
 * holds both the plan cards and the `Spec AI` card.
 *
 * Derived from a pathname rather than the router, because the banner renders
 * inside the follow panel, which mounts app-wide and is unit-tested without a
 * router. `/settings/billing` is a real route when there is no workspace slug
 * in the path, so this always resolves to the same page.
 */
export function specManageAiHref(pathname: string): string {
  return getWorkspaceScopedPath(pathname, '/settings/billing');
}
